import { applyDamage, rollDamage } from '../combat';
import { ZERO_STATS, type Monster, type Player, type Summon, type Vec2 } from '../types';
import { navField, steer } from '../map/nav';
import { clampToWalkable, entitiesOfKind, getPlayer, type World } from '../world';
import { monsterTarget } from './summons';

export const CHARGE_WINDUP = 0.6, CHARGE_DASH = 0.5, CHARGE_RECOVER = 0.6, CHARGE_MIN_DIST = 120, CHARGE_SPEED_MULT = 3, CHARGE_DAMAGE_MULT = 1.5;
const HIT_TOLERANCE = 12;

function strike(world: World, m: Monster, target: Player | Summon, mult = 1): void {
  const dmg = Math.floor(rollDamage(world.rng, m.contactDamage, ZERO_STATS, {}) * mult);
  applyDamage(world, target, dmg, m.contactDamage.element, target.pos, 'monster');
}

/** Le charger ne charge que le joueur : `dist` est sa distance au joueur, `startDir` la direction de la charge. */
function chargeStep(world: World, m: Monster, p: Player, dt: number, dist: number, minDist: number, startDir: Vec2): boolean {
  const c = m.charge!;
  if (c.phase === 'none') {
    c.cooldown = Math.max(0, c.cooldown - dt);
    if (c.cooldown > 0 || dist <= CHARGE_MIN_DIST || !p.alive) return false;
    c.phase = 'windup'; c.timer = CHARGE_WINDUP; c.dir = { ...startDir }; c.hitDone = false;
    m.anim = 'attack'; m.animTime = 0; m.vel = { x: 0, y: 0 };
    world.events.push({ type: 'bossCharge', pos: { ...m.pos }, dir: { ...c.dir } });
    return true;
  }
  c.timer -= dt;
  if (c.phase === 'windup') {
    if (c.timer <= 0) { c.phase = 'dash'; c.timer = CHARGE_DASH; m.anim = 'run'; m.animTime = 0; }
    return true;
  }
  if (c.phase === 'dash') {
    const v = m.speed * CHARGE_SPEED_MULT;
    m.vel = { x: c.dir.x * v, y: c.dir.y * v };
    m.pos = clampToWalkable({ x: m.pos.x + m.vel.x * dt, y: m.pos.y + m.vel.y * dt }, m.radius, world.room, m.pos);
    if (!c.hitDone && p.alive && Math.hypot(p.pos.x - m.pos.x, p.pos.y - m.pos.y) <= minDist + 8) { strike(world, m, p, CHARGE_DAMAGE_MULT); c.hitDone = true; }
    if (c.timer <= 0) { c.phase = 'recover'; c.timer = CHARGE_RECOVER; m.vel = { x: 0, y: 0 }; m.anim = 'idle'; }
    return true;
  }
  if (c.timer <= 0) { c.phase = 'none'; c.cooldown = world.content.monsters[m.defId]?.chargeCooldown ?? 4; }
  return true;
}

export function monsterSystem(world: World, dt: number): void {
  const p = getPlayer(world);
  if (!p) return;
  const grid = world.room.grid;
  if (grid) world.nav = navField(grid, p.pos, world.nav);
  for (const m of entitiesOfKind<Monster>(world, 'monster')) {
    if (!m.alive) continue;
    if (m.slow && world.time >= m.slow.until) m.slow = null;
    const speed = m.slow ? m.speed * m.slow.factor : m.speed;
    m.attackCooldown = Math.max(0, m.attackCooldown - dt);
    // cible : le joueur, ou l'invocation qui raille la plus proche
    const target = monsterTarget(world, m, p);
    const dx = target.pos.x - m.pos.x, dy = target.pos.y - m.pos.y;
    const dist = Math.hypot(dx, dy);
    const minDist = m.radius + target.radius;
    const range = m.range + target.radius;
    if (m.windup > 0) {
      m.windup -= dt;
      if (m.windup <= 0 && target.alive && dist <= range + HIT_TOLERANCE) strike(world, m, target);
      continue;
    }
    if (dist > 0 && (!m.charge || m.charge.phase === 'none')) {
      // contournement des obstacles (cible = joueur) : vers la cellule voisine la plus proche du joueur si la ligne droite est barrée ; vers une invocation, ligne droite
      m.facing = target === p && grid && dist > minDist + 30 ? steer(grid, world.nav, m.pos, p.pos, m.radius) : { x: dx / dist, y: dy / dist };
    }
    if (m.charge) {
      // le charger ne charge que le joueur, même quand une invocation le raille
      const dxp = p.pos.x - m.pos.x, dyp = p.pos.y - m.pos.y;
      const distP = target === p ? dist : Math.hypot(dxp, dyp);
      const dirP = target === p || distP === 0 ? m.facing : { x: dxp / distP, y: dyp / distP };
      if (chargeStep(world, m, p, dt, distP, m.radius + p.radius, dirP)) continue;
    }
    if (!target.alive) { m.vel = { x: 0, y: 0 }; if (m.anim === 'run') m.anim = 'idle'; continue; }
    if (dist > minDist) {
      const step = Math.min(speed * dt, dist - minDist);
      m.vel = { x: m.facing.x * speed, y: m.facing.y * speed };
      m.pos = clampToWalkable({ x: m.pos.x + m.facing.x * step, y: m.pos.y + m.facing.y * step }, m.radius, world.room, m.pos);
      if (m.anim === 'idle') m.anim = 'run';
    } else {
      m.vel = { x: 0, y: 0 };
      if (dist < minDist) m.pos = clampToWalkable({ x: target.pos.x - m.facing.x * minDist, y: target.pos.y - m.facing.y * minDist }, m.radius, world.room);
      if (m.anim === 'run') m.anim = 'idle';
    }
    if (dist <= range && m.attackCooldown <= 0) {
      m.attackCooldown = m.attackCooldownMax;
      m.windup = m.windupMax;
      m.anim = 'attack'; m.animTime = 0; m.vel = { x: 0, y: 0 };
    }
  }
}
